import moment from 'moment-timezone';

const teamChatCounterKey = 'teamCounter';
const supportChatCounterKey = 'supportChatCounter';

export const getMessageSentTime = (timeSent: number) => {
  const date = moment(timeSent);
  let format = 'h:mm A';
  if (moment().diff(date, 'days') > 1) {
    format = 'MM/DD/YY h:mm A';
  }
  return date.format(format);
};

export const decodeHtmlCharCodes = (str: string) => {
  return str.replace(/(&#(\d+);)/g, (match, capture, charCode) => {
    return String.fromCharCode(charCode as number);
  });
};

export const getRightOffset = (element: HTMLDivElement): Record<string, string> => {
  const supportButtonLeft = element.offsetLeft + element.offsetWidth;
  const supportButtonParentLeft = element.parentElement?.parentElement?.offsetWidth || 0;
  const finalLeft = supportButtonParentLeft ? supportButtonParentLeft - supportButtonLeft : supportButtonLeft;
  const style = { right: `${finalLeft}px` };
  return style;
};

export const getTeamChatCounterKey = (eventName: string) => `${teamChatCounterKey}-${eventName}`;
export const getSupportChatCounterKey = (eventName: string) => `${supportChatCounterKey}-${eventName}`;

/**
 * Shape of a resolved AI Teammate async result fetched from S3. The optional
 * pendingConfirmation is present when the assistant needs the user to confirm or
 * reject an action before it proceeds.
 */
export interface AsyncTeammateResult {
  sessionId: string;
  text: string;
  pendingConfirmation?: { type: string; challengeId: string };
  escalation?: { reason: string; challengeId?: string };
  followUpQuestions?: string[];
}

export const ASYNC_FETCH_DELAYS_IN_MS = [3000, 2000, 2000, 1000];
export const MAX_ASYNC_FETCH_ATTEMPTS = 35;

/**
 * Polls an S3 URL for the result of an asynchronous AI Teammate job, retrying with a
 * backoff while the object is not yet available (HTTP 404).
 *
 * Resolves with the parsed JSON result on a 2xx response.
 * Rejects on a missing s3Url, exhausted retries, a non-404 error response (with the
 * response body text as the reason), or any network/parse error.
 *
 * The delay before each attempt is taken from ASYNC_FETCH_DELAYS_IN_MS, clamped to the
 * last entry once attempts exceed the array length.
 */
export const fetchAsyncWithRetry = (s3Url: string | undefined, attempt = 0): Promise<AsyncTeammateResult> => {
  return new Promise((resolve, reject) => {
    if (!s3Url) {
      reject(new Error('Missing s3Url for async result'));
      return;
    }
    if (attempt >= MAX_ASYNC_FETCH_ATTEMPTS) {
      reject(new Error(`Max retries reached for ${s3Url}`));
      return;
    }
    setTimeout(() => {
      fetch(s3Url)
        .then((r) => {
          if (r.ok) {
            r.json().then(resolve).catch(reject);
          } else if (r.status !== 404) {
            r.text()
              .then((t) => reject(new Error(t)))
              .catch(reject);
          } else {
            fetchAsyncWithRetry(s3Url, attempt + 1)
              .then(resolve)
              .catch(reject);
          }
        })
        .catch(reject);
    }, ASYNC_FETCH_DELAYS_IN_MS[Math.min(attempt, ASYNC_FETCH_DELAYS_IN_MS.length - 1)]);
  });
};
